/**
 * The real-vs-simulated boundary, stated to the visitor.
 *
 * This is not boilerplate and should not be trimmed to fit a layout. The trial
 * shows scored candidates with skills, experience and a distance from the
 * office; every one of those is a claim, and three of them are stand-ins. A
 * prospect who takes the results at face value has been misled by us, not by
 * their own carelessness.
 *
 * Carried over from the prototype's footer, with one substantive change. The
 * prototype said candidate names and contact details "stay hidden until you
 * unmask". The synthetic records have no name, email or phone at all -- see the
 * assertion in match.test.ts -- so that sentence described a mask over nothing.
 * The wording below describes the boundary that genuinely exists instead, and
 * stays true when real sourcing replaces the synthetic set.
 */

import { MASK_HAS_NOTHING_BEHIND_IT } from '../../data/candidates';
import styles from './index.module.css';

const SimulationNotice = () => (
  <aside
    className={styles.notice}
    aria-label="What is real and what is simulated"
  >
    <p className={styles.lead}>
      <strong>What this trial is.</strong> The flow you just went through is
      real: the rate card, the contract arithmetic, and the boundary that keeps
      candidate identity out of results until a plan is active.
    </p>
    <dl className={styles.list}>
      <div className={styles.row}>
        <dt className={styles.simulated}>Simulated</dt>
        <dd>
          The candidates. Twelve fictional profiles, held locally. No Nexxt
          sourcing and no Bedrock model call happen here.
        </dd>
      </div>
      <div className={styles.row}>
        <dt className={styles.simulated}>Simulated</dt>
        <dd>
          The match scores. A deterministic keyword, experience and education
          model — not the deep-matching pipeline that scores real candidates.
        </dd>
      </div>
      <div className={styles.row}>
        <dt className={styles.simulated}>Simulated</dt>
        <dd>
          Distance from your office. A placeholder figure, not a geocoded
          distance from the address you typed.
        </dd>
      </div>
      <div className={styles.row}>
        <dt className={styles.real}>Real</dt>
        <dd>
          {MASK_HAS_NOTHING_BEHIND_IT
            ? 'The identity boundary. These profiles carry no name or contact details at all, and the live product withholds them the same way until a plan is active.'
            : 'The identity boundary. Names and contact details are withheld until a plan is active.'}
        </dd>
      </div>
      <div className={styles.row}>
        <dt className={styles.real}>Real</dt>
        <dd>
          Your rate and contract terms, calculated from the answers you gave.
          The rate card itself is pending final sign-off.
        </dd>
      </div>
    </dl>
  </aside>
);

export default SimulationNotice;
